import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { SabotageCatalogEntry } from '@/types/sabotageStats';
import { trackEvent } from '@/lib/analytics';

export interface AppliedSabotage {
  id: string;
  league_id: string;
  matchup_id: string | null;
  applied_by_team_id: string;
  target_team_id: string;
  sabotage_name: string;
  sabotage_icon: string;
  sabotage_description: string | null;
  effect_type: string;
  effect_config: Record<string, any>;
  week: number | null;
  cost: number;
  status: string;
  applied_at: string;
  expires_at: string | null;
}

// Re-export from centralized catalog for backward compat
export { MLB_MATCHUP_SABOTAGES as mlbMatchupSabotages } from '@/data/sabotageCatalog';

export function useSabotageEngine(leagueId: string | null) {
  const [sabotages, setSabotages] = useState<AppliedSabotage[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchSabotages = useCallback(async (week?: number) => {
    if (!leagueId) return;
    setLoading(true);

    try {
      let query = supabase
        .from('applied_sabotages')
        .select('*')
        .eq('league_id', leagueId)
        .order('applied_at', { ascending: false });

      if (week) {
        query = query.eq('week', week);
      }

      const { data, error } = await query;
      if (error) throw error;
      setSabotages((data || []) as unknown as AppliedSabotage[]);
    } catch (err) {
      console.error('Failed to fetch sabotages:', err);
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  const applySabotage = useCallback(async (
    matchupId: string,
    targetTeamId: string,
    sabotage: SabotageCatalogEntry
  ) => {
    if (!leagueId) throw new Error('No league');

    const { data: result, error } = await supabase.functions.invoke('apply-sabotage', {
      body: {
        type: 'matchup',
        league_id: leagueId,
        matchup_id: matchupId,
        target_team_id: targetTeamId,
        sabotage: {
          name: sabotage.name,
          icon: sabotage.icon,
          description: sabotage.description,
          effect_type: sabotage.effect_type,
          effect_config: sabotage.effect_config,
        },
      },
    });

    // SAB-413: supabase-js v2 wraps non-2xx responses in FunctionsHttpError
    // with a generic "Edge Function returned a non-2xx status code" message.
    // The structured `{ error: '...' }` body the edge function actually
    // returns lives on `error.context` (the raw Response). Read it so the
    // toast shows the user-readable message ("Legendary sabotage limit
    // reached (1/week)…") instead of the wrapper class's generic.
    // Same pattern as SAB-394 in NFLFundsSection's callEdgeFunction.
    if (error) {
      let errMsg = error.message || 'Failed to apply sabotage';
      try {
        const resp = (error as any).context as Response | undefined;
        if (resp && typeof resp.json === 'function') {
          const errBody = await resp.json();
          if (errBody?.error) errMsg = errBody.error;
        }
      } catch { /* body empty or unparseable — fall through with message-only */ }
      throw new Error(errMsg);
    }
    if (result?.error) throw new Error(result.error);

    trackEvent('sabotage_card_applied', { card_name: sabotage.name, matchup_id: matchupId });

    // Refresh
    await fetchSabotages();
    return result;
  }, [leagueId, fetchSabotages]);

  // Realtime updates
  useEffect(() => {
    if (!leagueId) return;

    const channel = supabase
      .channel(`sabotage-updates-${leagueId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'applied_sabotages', filter: `league_id=eq.${leagueId}` },
        () => { fetchSabotages().catch(err => console.error('[SabotageEngine] realtime refresh error:', err)); }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [leagueId]);

  return { sabotages, loading, fetchSabotages, applySabotage };
}
